/**
 * Google Analytics integration with cookie consent management
 * Only loads GA when user has explicitly consented to analytics cookies
 */

// Google Analytics Measurement ID from environment variables
export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

/**
 * Get current cookie consent status from localStorage
 */
export const getCookieConsent = (): 'accepted' | 'declined' | null => {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('finley-cookie-consent');
  return stored as 'accepted' | 'declined' | null;
};

/**
 * Check if analytics should be loaded based on consent
 */
export const shouldLoadAnalytics = (): boolean => {
  if (!GA_MEASUREMENT_ID) return false;
  const consent = getCookieConsent();
  return consent === 'accepted';
};

/**
 * Initialize Google Analytics
 * Loads the gtag.js script and configures GA with privacy settings
 */
export const initGA = () => {
  if (!shouldLoadAnalytics()) return;

  // Check if already loaded
  if (window.gtag) return;

  // Load gtag.js script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true, // Anonymize IPs for privacy
    cookie_flags: 'SameSite=None;Secure', // Modern cookie settings
  });
};

/**
 * Remove Google Analytics scripts and cookies
 * Called when user declines or withdraws consent
 */
export const removeGA = () => {
  // Remove gtag scripts
  const scripts = document.querySelectorAll(`script[src*="googletagmanager.com"]`);
  scripts.forEach(script => script.remove());

  // Remove GA cookies
  const gaCookies = document.cookie.split(';').filter(cookie =>
    cookie.trim().startsWith('_ga') || cookie.trim().startsWith('_gid')
  );

  gaCookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });

  // Clear dataLayer
  if (window.dataLayer) {
    window.dataLayer = [];
  }

  // Remove gtag reference
  if (window.gtag) {
    delete window.gtag;
  }
};

/**
 * Track page view
 * Called on route changes
 */
export const trackPageView = (url: string) => {
  if (!shouldLoadAnalytics() || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
  });
};

/**
 * Track custom event
 * @param action - Event action (e.g., 'click', 'submit')
 * @param category - Event category (e.g., 'download', 'navigation')
 * @param label - Optional event label
 * @param value - Optional numeric value
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (!shouldLoadAnalytics() || !window.gtag) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

// Type augmentation for window.gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

